import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/Auth/AuthContext";
import toastError from "../errors/toastError";
import api from "../services/api";

/**
 * Ações de encerramento/devolução de ticket, compartilhadas entre a tela de
 * conversa (TicketActionButtons) e a lista (TicketListItem).
 *
 * A decisão "exige GLPI?" e "pode finalizar sem atendimento?" é sempre feita com
 * dados frescos de GET /tickets/:id: o ticket da lista/socket pode chegar sem
 * `glpiEnabled`/`glpiTicketId`, o que fazia os dois botões divergirem.
 *
 * Cada ação recebe callbacks para abrir a UI necessária (drawer/diálogo GLPI,
 * confirmação), pois quem renderiza é o componente.
 */
const useTicketActions = (ticket) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { user } = useContext(AuthContext);
	const [loading, setLoading] = useState(false);

	const fetchFreshTicket = async () => {
		try {
			const { data } = await api.get(`/tickets/${ticket.id}`);
			return data;
		} catch (err) {
			// verificação falhou: usa o que o componente já tem
			return ticket;
		}
	};

	const requiresGlpi = (data) => Boolean(data?.glpiEnabled || data?.glpiTicketId);

	const putTicket = async (payload) => {
		setLoading(true);
		try {
			await api.put(`/tickets/${ticket.id}`, payload);
			return true;
		} catch (err) {
			toastError(err, t);
			return false;
		} finally {
			setLoading(false);
		}
	};

	/** "Finalizar com atendimento": GLPI (drawer/diálogo) ou confirmação simples. */
	const requestCloseWithService = async ({ onNeedGlpi, onNeedConfirm }) => {
		const fresh = await fetchFreshTicket();
		if (requiresGlpi(fresh)) {
			onNeedGlpi();
		} else {
			onNeedConfirm();
		}
	};

	const confirmCloseWithService = async () => {
		const ok = await putTicket({ status: "closed", userId: user?.id });
		if (ok) navigate("/tickets");
		return ok;
	};

	/**
	 * "Finalizar sem atendimento": só para ticket sem trâmite, tarefa ou chamado GLPI
	 * (regra validada também no backend).
	 */
	const requestQuickClose = async ({ onAllowed, onDenied }) => {
		const fresh = await fetchFreshTicket();
		if (fresh?.quickCloseAllowed) {
			onAllowed();
		} else {
			onDenied();
		}
	};

	const quickClose = async (closeReason) => {
		const ok = await putTicket({
			status: "closed",
			userId: user?.id,
			closeReason: closeReason.trim()
		});
		if (ok) navigate("/tickets");
		return ok;
	};

	/** Devolve para pendente; com GLPI e trâmites do atendente, pede o diálogo de devolução. */
	const returnToPending = async ({ onNeedGlpiReturn }) => {
		const fresh = await fetchFreshTicket();
		if (requiresGlpi(fresh) && fresh.hasAttendantMessages) {
			onNeedGlpiReturn();
			return false;
		}
		const ok = await putTicket({ status: "pending", userId: null });
		if (ok) navigate("/tickets");
		return ok;
	};

	return {
		loading,
		requestCloseWithService,
		confirmCloseWithService,
		requestQuickClose,
		quickClose,
		returnToPending
	};
};

export default useTicketActions;
